import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../../components/Navbar";
import Footer from "../../components/Footer";
import "./Home.css";
import { Sun, Moon, ClipboardList, Timer, Users, Target, Clock, BarChart3, Handshake, User, LogOut, ChevronDown } from 'lucide-react';
import { logout } from "../../services/authUtils";

function Home() {
    const navigate = useNavigate();
    const [darkMode, setDarkMode] = useState(false);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [userProfile, setUserProfile] = useState({
        displayName: "User",
        username: ""
    });

    React.useEffect(() => {
        const storedUser = localStorage.getItem("user");
        if (storedUser) {
            setIsAuthenticated(true);
            try {
                const user = JSON.parse(storedUser);
                let name = "User";
                if (user.firstName && user.lastName) {
                    name = `${user.firstName} ${user.lastName}`;
                } else {
                    name = user.firstName || user.name || user.username || user.userName || user.email || "User";
                }
                const handle = user.username || user.userName || "";
                setUserProfile({
                    displayName: name,
                    username: handle ? `@${handle.replace(/^@/, '')}` : ""
                });
            } catch (e) {
                console.error("Error parsing user data:", e);
            }
        }
    }, []);

    const handleLogout = () => {
        logout("");
    };

    const toggleDarkMode = () => {
        setDarkMode(!darkMode);
    };

    return (
        <div className={`home-container ${darkMode ? 'dark-mode' : ''}`}>
            <div className="bg-animation">
                <div className="bg-shapes">
                    <div className="shape"></div>
                    <div className="shape"></div>
                    <div className="shape"></div>
                    <div className="shape"></div>
                </div>
            </div>

            {/* Navbar */}
            <nav className="navbar">
                <div className="logo-container">
                    <svg
                        className="product-icon"
                        viewBox="0 0 100 100"
                        xmlns="http://www.w3.org/2000/svg"
                        onClick={() => navigate('/')}
                        style={{ cursor: 'pointer' }}
                    >
                        <path
                            d="M 20 35 L 45 35"
                            stroke="#1E40AF"
                            strokeWidth="4"
                            strokeLinecap="round"
                        ></path>
                        <path
                            d="M 20 50 L 55 50"
                            stroke="#14B8A6"
                            strokeWidth="4"
                            strokeLinecap="round"
                        ></path>
                        <path
                            d="M 20 65 L 65 65"
                            stroke="#F59E0B"
                            strokeWidth="4"
                            strokeLinecap="round"
                        ></path>
                        <path d="M 45 35 L 55 40 L 45 45 Z" fill="#1E40AF"></path>
                        <path d="M 55 50 L 65 55 L 55 60 Z" fill="#14B8A6"></path>
                        <path d="M 65 65 L 75 70 L 65 75 Z" fill="#F59E0B"></path>
                        <circle cx="80" cy="50" r="8" fill="#1E40AF"></circle>
                    </svg>
                    <span className="logo-text">SequFlow</span>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                    <button
                        onClick={toggleDarkMode}
                        style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '1.2rem',
                            padding: '5px',
                            borderRadius: '50%',
                            transition: 'background 0.3s'
                        }}
                        title="Toggle Dark Mode"
                    >
                        {darkMode ? <Sun size={20} color="#FFD700" /> : <Moon size={20} color="#666" />}
                    </button>
                    {isAuthenticated ? (
                        <div className="user-profile-container" style={{ position: 'relative' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', padding: '5px 10px', borderRadius: '20px', backgroundColor: darkMode ? '#333' : '#f0f0f0' }}>
                                <div className="user-profile-small" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '32px', height: '32px', borderRadius: '50%', backgroundColor: '#0052CC', color: 'white' }}>
                                    <User size={16} />
                                </div>
                                <span className="navbar-user-name-label" style={{ fontWeight: '500', fontSize: '14px', color: darkMode ? '#fff' : '#333' }}>{userProfile.displayName}</span>
                                <ChevronDown size={14} className="dropdown-arrow-icon" style={{ color: darkMode ? '#aaa' : '#666' }} />

                                <div className="profile-dropdown" style={{ position: 'absolute', top: '100%', right: '0', marginTop: '10px', backgroundColor: darkMode ? '#222' : '#fff', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', width: '200px', zIndex: 1000, padding: '10px 0' }}>
                                    <div className="dropdown-header" style={{ padding: '10px 20px', display: 'flex', flexDirection: 'column' }}>
                                        <span className="user-name" style={{ fontWeight: '600', fontSize: '14px', color: darkMode ? '#fff' : '#333' }}>{userProfile.displayName}</span>
                                        {userProfile.username && (
                                            <span className="user-handle" style={{ fontSize: '12px', color: '#6B778C', marginTop: '4px' }}>{userProfile.username}</span>
                                        )}
                                    </div>
                                    <div className="dropdown-divider" style={{ height: '1px', backgroundColor: darkMode ? '#444' : '#E5E7EB', margin: '5px 0' }}></div>
                                    <button className="dropdown-item logout-btn" onClick={handleLogout} style={{ display: 'flex', alignItems: 'center', gap: '10px', width: '100%', padding: '10px 20px', border: 'none', background: 'none', cursor: 'pointer', color: '#EF4444', fontSize: '14px', textAlign: 'left', transition: 'background-color 0.2s' }}>
                                        <LogOut size={16} />
                                        <span>Log out</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <button className="nav-btn" onClick={() => navigate('/signup')}>Get Started</button>
                    )}
                </div>
            </nav>

            <section className="hero">
                <h1>Manage Projects.<br />Track Every Minute.</h1>
                <p className="subtitle">Transform your workflow with intelligent project management and precise time tracking. Collaborate seamlessly and boost productivity.</p>
                <div className="hero-cta">
                    {isAuthenticated ? (
                        <a href="#" className="primary-btn" onClick={(e) => { e.preventDefault(); navigate('/tasks'); }}>Go to Task Page</a>
                    ) : (
                        <>
                            <a href="#" className="primary-btn" onClick={(e) => { e.preventDefault(); navigate('/signup'); }}>Start Free Trial</a>
                            <a href="#" className="secondary-btn">Watch Demo</a>
                        </>
                    )}
                </div>

                <div className="stats-container">
                    <div className="stat-card">
                        <div className="stat-icon"><ClipboardList /></div>
                        <div className="stat-number">150K+</div>
                        <div className="stat-label">Projects Managed</div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-icon"><Timer /></div>
                        <div className="stat-number">800K+</div>
                        <div className="stat-label">Hours Tracked</div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-icon"><Users /></div>
                        <div className="stat-number">50K+</div>
                        <div className="stat-label">Teams Collaborating</div>
                    </div>
                </div>
            </section>

            <section className="features">
                <div className="features-container">
                    <h2 className="section-title">Everything You Need</h2>
                    <p className="section-subtitle">Powerful features designed to streamline your workflow and maximize team productivity.</p>

                    <div className="features-grid">
                        <div className="feature-card" onClick={() => navigate('/task-management')} style={{ cursor: 'pointer' }}>
                            <div className="feature-content">
                                <div className="feature-icon"><Target /></div>
                                <h3>Smart Task Management</h3>
                                <p>Organize, prioritize, and track tasks with intelligent automation. Set dependencies, milestones, and never miss a deadline.</p>
                            </div>
                        </div>

                        <div className="feature-card">
                            <div className="feature-content">
                                <div className="feature-icon"><Clock /></div>
                                <h3>Precision Time Tracking</h3>
                                <p>Log billable and non-billable hours effortlessly. Automatic timesheets with manual and timer options for perfect accuracy.</p>
                            </div>
                        </div>

                        <div className="feature-card">
                            <div className="feature-content">
                                <div className="feature-icon"><BarChart3 /></div>
                                <h3>Real-Time Analytics</h3>
                                <p>Visualize project progress with dynamic dashboards. Make data-driven decisions with comprehensive reporting tools.</p>
                            </div>
                        </div>

                        <div className="feature-card">
                            <div className="feature-content">
                                <div className="feature-icon"><Handshake /></div>
                                <h3>Seamless Collaboration</h3>
                                <p>Connect your team with integrated chat, file sharing, and real-time updates. Work together, anywhere.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className="timesheet-showcase">
                <div className="showcase-container">
                    <div className="showcase-content">
                        <h2>Track Time Like Never Before</h2>
                        <p>Our intuitive timesheet module makes logging hours a breeze. Whether you're billing clients or tracking internal projects, every minute counts.</p>
                        <p>Integrate seamlessly with invoicing, get automatic reminders, and generate detailed reports with just a few clicks.</p>
                        <a href="#" className="primary-btn">Learn More About Time Tracking</a>
                    </div>

                    <div className="showcase-visual">
                        <div className="time-blocks">
                            <div className="time-block">
                                <div className="time-block-time">5:45</div>
                                <div className="time-block-label">Project A</div>
                            </div>
                            <div className="time-block">
                                <div className="time-block-time">3:30</div>
                                <div className="time-block-label">Client Call</div>
                            </div>
                            <div className="time-block">
                                <div className="time-block-time">8:00</div>
                                <div className="time-block-label">Development</div>
                            </div>
                            <div className="time-block">
                                <div className="time-block-time">2:15</div>
                                <div className="time-block-label">Meeting</div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            <Footer />
        </div>
    );
}

export default Home;
